// src/components/core-components/Topbar.js
import React, { useEffect, useRef, useState } from "react";
import "./Topbar.css";
import NextCalendarEvent from "../Calendar/NextCalendarEvent";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../Security/firebase-config";

const LOGO_SRC = "/assets/valovate_logo_full.svg";

const FALLBACK_ANNOUNCEMENTS = [
  {
    id: "discord",
    text: "Join the Discord to find teammates + get updates.",
    url: "https://discord.gg/GrCvYvqFVF",
  },
];

const Topbar = () => {
  const [announcements, setAnnouncements] = useState(FALLBACK_ANNOUNCEMENTS);
  const [showAnnouncementBanner, setShowAnnouncementBanner] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [announcementIndex, setAnnouncementIndex] = useState(0);

  const textViewportRef = useRef(null);
  const textRef = useRef(null);

  const announcement = announcements[announcementIndex] || null;
  const announcementId = announcement?.id ?? null;

  const openAnnouncement = (item) => {
    if (!item?.url) return;

    if (item.internal) {
      window.location.href = item.url;
      return;
    }

    window.open(item.url, "_blank", "noopener,noreferrer");
  };

  const goNextAnnouncement = () => {
    setAnnouncementIndex((index) => {
      const length = announcements.length || 1;
      return (index + 1) % length;
    });
  };

  const goPrevAnnouncement = () => {
    setAnnouncementIndex((index) => {
      const length = announcements.length || 1;
      return (index - 1 + length) % length;
    });
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const handleChange = (event) => setShowAnnouncementBanner(event.matches);

    setShowAnnouncementBanner(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  useEffect(() => {
    if (!showAnnouncementBanner || !announcementId) return undefined;

    const measure = () => {
      const viewport = textViewportRef.current;
      const textElement = textRef.current;
      if (!viewport || !textElement) return;
      setIsOverflowing(textElement.scrollWidth > viewport.clientWidth);
    };

    measure();
    requestAnimationFrame(measure);

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [announcementId, showAnnouncementBanner]);

  useEffect(() => {
    if (!showAnnouncementBanner || !announcements.length) return undefined;

    const intervalMs = 40000;
    const intervalId = window.setInterval(() => {
      setAnnouncementIndex((index) => {
        const length = announcements.length || 1;
        return (index + 1) % length;
      });
    }, intervalMs);

    return () => window.clearInterval(intervalId);
  }, [announcements.length, showAnnouncementBanner]);

  useEffect(() => {
    const announcementsRef = collection(db, "siteAnnouncements");
    const announcementsQuery = query(
      announcementsRef,
      where("enabled", "==", true),
      orderBy("order", "asc")
    );

    const unsubscribe = onSnapshot(
      announcementsQuery,
      (snapshot) => {
        const now = Date.now();

        const items = snapshot.docs
          .map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }))
          .filter((item) => {
            const startsAtMs = item.startsAt?.toMillis ? item.startsAt.toMillis() : null;
            const endsAtMs = item.endsAt?.toMillis ? item.endsAt.toMillis() : null;

            if (startsAtMs && now < startsAtMs) return false;
            if (endsAtMs && now > endsAtMs) return false;
            if (item.showOnWebsite === false) return false;

            return Boolean(item.text);
          });

        setAnnouncements(items.length ? items : FALLBACK_ANNOUNCEMENTS);
        setAnnouncementIndex((index) => {
          const length = items.length || FALLBACK_ANNOUNCEMENTS.length;
          return length ? index % length : 0;
        });
      },
      (error) => {
        console.error("siteAnnouncements snapshot failed:", error);
        setAnnouncements(FALLBACK_ANNOUNCEMENTS);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <nav className="Topbar">
      <div className="topbar-container">
        <div className="topbar-left">
          <a href="/" className="topbar-logo">
            <img
              src={LOGO_SRC}
              alt="Valovate logo"
              className="topbar-logo-img"
              loading="eager"
              decoding="async"
            />
          </a>
        </div>

        <div className="topbar-middle">
          <div className="topbar-middle-inner">
            <div className="next-event next-event--desktop">
              <NextCalendarEvent />
            </div>

            {showAnnouncementBanner && announcement && (
              <div
                className={`TopbarAnnouncement ${isOverflowing ? "is-overflowing" : ""}`}
                role="button"
                tabIndex={0}
                onClick={() => openAnnouncement(announcement)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openAnnouncement(announcement);
                  }
                }}
                title={announcement.text}
              >
                <button
                  className="TopbarAnnouncement__nav"
                  type="button"
                  aria-label="Previous announcement"
                  onClick={(event) => {
                    event.stopPropagation();
                    goPrevAnnouncement();
                  }}
                >
                  {"<"}
                </button>

                <div className="TopbarAnnouncement__textViewport" ref={textViewportRef}>
                  {isOverflowing ? (
                    <div key={announcement.id} className="TopbarAnnouncement__track">
                      <span ref={textRef} className="TopbarAnnouncement__text">
                        {announcement.text}
                      </span>
                      <span className="TopbarAnnouncement__gap" aria-hidden="true" />
                      <span className="TopbarAnnouncement__text" aria-hidden="true">
                        {announcement.text}
                      </span>
                      <span className="TopbarAnnouncement__gap" aria-hidden="true" />
                    </div>
                  ) : (
                    <div className="TopbarAnnouncement__textStatic" ref={textRef}>
                      {announcement.text}
                    </div>
                  )}
                </div>

                <button
                  className="TopbarAnnouncement__nav"
                  type="button"
                  aria-label="Next announcement"
                  onClick={(event) => {
                    event.stopPropagation();
                    goNextAnnouncement();
                  }}
                >
                  {">"}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="topbar-right">
          <div className="next-event next-event--mobile">
            <NextCalendarEvent />
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Topbar;
